import { useContext } from 'react';
import { AuthContext } from '@/context/auth.context';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin } from '@/utils/user';
import { useIsModuleEnabled } from '@/hooks/organization-config/useOrganizationEnabledModules';
import { useFamousWorkflowIntegrationEnabled } from 'src/lib/workflows/useFamousWorkflowIntegrationEnabled';
import { useCanMany } from '@/hooks/useCanMany';
import { useFeatureFlags } from '@/integrations/feature-flags/feature-flags';
import type { FeatureFlagKey } from '@erp/shared-types';
import {
  getOrganizationSettingsSections,
  ORGANIZATION_SETTINGS_PERMISSION_CHECKS,
  STAFF_SECTIONS_PERMISSION,
  type OrganizationSettingsSection,
} from './organizationSettingsSections';

/**
 * The organization-settings sections the current viewer may open, judged by
 * each section's own gate (Gently staff, module, feature flag, permission).
 * One source of truth for the settings page's rail and for whether the
 * "Manage Organization" entry appears at all — a user with any grant that
 * unlocks a section (Read Contact, Update OrganizationConfig, Read
 * MemoryRule…) gets in, not only the historical User / UserGroup / ApiKey
 * readers.
 */
export function useVisibleOrganizationSettingsSections(): OrganizationSettingsSection[] {
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const { isModuleEnabled } = useIsModuleEnabled();
  const famousIntegrationEnabled = useFamousWorkflowIntegrationEnabled();
  // Every registered flag, so a section's `featureFlag` resolves by
  // construction — see `useFeatureFlags`.
  const featureFlags = useFeatureFlags();
  const { can } = useCanMany(ORGANIZATION_SETTINGS_PERMISSION_CHECKS);

  return getOrganizationSettingsSections({
    gently: isAdmin(currentUser, viewMode),
    canManageStaffSections: can(
      STAFF_SECTIONS_PERMISSION.action,
      STAFF_SECTIONS_PERMISSION.subject,
    ),
    isModuleEnabled,
    hasPermission: can,
    isFeatureFlagEnabled: (flag: FeatureFlagKey) => featureFlags[flag],
    famousIntegrationEnabled,
  });
}
